/*
 * Notify-me modal — restock signup + remembered signups.
 *
 * Primary path: POST { email, productId, variantId } to the wishlist worker
 * through the App Proxy (/apps/wishlist/restock/subscribe — same origin, so
 * tracker blockers don't touch it). The worker's hourly cron sends ONE
 * "Back in Stock Digest" Klaviyo event per email covering every product of
 * theirs that came back, with a per-email cooldown, so a shopper waiting on
 * five products gets one email, not five (apps/wishlist/README.md).
 *
 * The modal's form is a plain <form>, NOT a Shopify customer form: Shopify's
 * bot-protection script hooks every storefront form posting to /contact and
 * re-posts it natively (page reload, no success panel) — that was the
 * "popup vanishes, button never updates" bug. A rejected email (400) shows
 * an inline error; nothing falls back for it.
 *
 * Fallback path (worker unreachable — network, 5xx, proxy not routed): a
 * hidden Shopify customer form with the legacy restock-* tags, posted in the
 * background. When Shopify bot-challenges that (302 → /challenge) it's
 * submitted natively instead; the modal id is parked in sessionStorage so
 * the reloaded page re-opens the modal on its success panel.
 *
 * Signups are remembered in localStorage (product handles only — no PII) so
 * "Notify me" triggers render disabled with a done label on later visits.
 *
 * The same modal (snippets/kkumi-notify-modal.liquid) is used by the v3
 * product card and the PDP hero.
 *
 * Signed in (form[data-kk-notify-account]): the email box comes filled in with
 * the account email and hidden, so one tap signs up. If the worker turns that
 * email down, the box is shown with the error so it can be corrected.
 *
 * Liquid values (success copy, done label, product handle, modal id) arrive
 * via data-* attributes, per the repo convention — this file contains zero
 * Liquid.
 */
(function () {
  'use strict';

  var FORM_SELECTOR = 'form[data-kk-notify]';
  var TRIGGER_SELECTOR = '[data-kk-notify-trigger]';
  var STORAGE_KEY = 'kk-notify-signups';
  var PENDING_KEY = 'kk-notify-pending';
  var PENDING_MAX_AGE_MS = 5 * 60 * 1000;
  var DONE_LABEL = "We'll notify you";
  var RESTOCK_URL = '/apps/wishlist/restock/subscribe';
  var CHECK_SVG =
    '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor"' +
    ' stroke-width="3" stroke-linecap="round" stroke-linejoin="round">' +
    '<polyline points="20 6 9 17 4 12"/></svg>';

  /* ---- Remembered signups (localStorage, guarded for private mode) ----

     Stored as { key: signedUpAtMs }. A key is the product handle (card —
     the whole product was sold out) or "handle:variantId" (PDP — the
     selected option was sold out). A remembered signup is FORGOTTEN once
     the page shows that product/variant in stock (elements carrying
     data-kk-restock-available="key,key,…", emitted by the card and the PDP
     hero), so a later sell-out offers "Notify me" again instead of a stale
     done state. Entries older than SIGNUP_TTL_MS are dropped as a backstop
     for products never seen in stock again. */

  var SIGNUP_TTL_MS = 90 * 86400000;

  function readSignups() {
    var out = {};
    try {
      var raw = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
      var now = Date.now();
      if (Array.isArray(raw)) {
        // Legacy format: a bare list of handles, no timestamps.
        raw.forEach(function (k) { if (typeof k === 'string') out[k] = now; });
      } else if (raw && typeof raw === 'object') {
        Object.keys(raw).forEach(function (k) {
          var at = Number(raw[k]) || 0;
          if (now - at < SIGNUP_TTL_MS) out[k] = at;
        });
      }
    } catch (e) { /* unreadable — treat as empty */ }
    return out;
  }

  function writeSignups(map) {
    try {
      if (Object.keys(map).length) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) { /* private mode / quota — degrade to per-pageview state */ }
  }

  // Storage key for a signup form: handle, plus the variant when one was chosen.
  function formKey(form) {
    var handle = form.dataset.handle || '';
    var variant = form.dataset.kkRestockVariant || '';
    return handle && variant ? handle + ':' + variant : handle;
  }

  function recordSignup(key) {
    if (!key) return;
    var map = readSignups();
    map[key] = Date.now();
    writeSignups(map);
    syncTriggers();
  }

  // Forget signups the page shows as back in stock.
  function forgetAvailable() {
    var els = document.querySelectorAll('[data-kk-restock-available]');
    if (!els.length) return;
    var map = readSignups();
    var changed = false;
    for (var i = 0; i < els.length; i++) {
      var keys = (els[i].getAttribute('data-kk-restock-available') || '').split(',');
      for (var j = 0; j < keys.length; j++) {
        var k = keys[j].trim();
        if (k && map[k] != null) { delete map[k]; changed = true; }
      }
    }
    if (changed) writeSignups(map);
  }

  // Does a remembered signup cover this trigger? A whole-product signup
  // covers every variant; a variant signup marks the product's card too.
  function isSignedUp(map, key) {
    if (!key) return false;
    if (map[key] != null) return true;
    var colon = key.indexOf(':');
    if (colon !== -1) return map[key.slice(0, colon)] != null;
    var prefix = key + ':';
    return Object.keys(map).some(function (k) { return k.indexOf(prefix) === 0; });
  }

  /* Put every notify trigger in the state its signup record says: done
     (disabled + done label) or idle (original label restored). Two-way, so a
     PDP option change or a forgotten signup brings "Notify me" back. */
  function syncTriggers() {
    var map = readSignups();
    var triggers = document.querySelectorAll(TRIGGER_SELECTOR);
    for (var i = 0; i < triggers.length; i++) {
      var btn = triggers[i];
      var done = isSignedUp(map, btn.getAttribute('data-kk-notify-trigger'));
      var isDone = btn.getAttribute('data-kk-notify-done') === '1';
      if (done === isDone) continue;
      var label = btn.querySelector('span');
      if (done) {
        btn.setAttribute('data-kk-notify-label', label ? label.textContent : btn.textContent);
        btn.setAttribute('data-kk-notify-done', '1');
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        // Compact skin: ellipsis instead of overflow so the longer done
        // label fits narrow grid columns (kkumi-product-card-v3.css).
        btn.classList.add('kk-pc__btn--done');
        var doneText = btn.getAttribute('data-done-label') || DONE_LABEL;
        if (label) label.textContent = doneText;
        else btn.textContent = doneText;
      } else {
        var idle = btn.getAttribute('data-kk-notify-label') || '';
        btn.removeAttribute('data-kk-notify-done');
        btn.disabled = false;
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('kk-pc__btn--done');
        if (label) label.textContent = idle;
        else btn.textContent = idle;
      }
    }
  }

  // The PDP hero calls this after retargeting its trigger to a new variant.
  window.KKNotify = { sync: syncTriggers };

  /* ---- Re-open after a native-submit reload ---- */

  function parkPending(form) {
    var modal = form.closest('modal-dialog');
    if (!modal || !modal.id) return;
    try {
      window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({ id: modal.id, at: Date.now() }));
    } catch (e) { /* private mode — reload just won't re-open the modal */ }
  }

  function reopenPending() {
    var pending = null;
    try {
      pending = JSON.parse(window.sessionStorage.getItem(PENDING_KEY) || 'null');
      window.sessionStorage.removeItem(PENDING_KEY);
    } catch (e) { return; }
    if (!pending || !pending.id || Date.now() - (pending.at || 0) > PENDING_MAX_AGE_MS) return;
    var modal = document.getElementById(pending.id);
    if (!modal || typeof modal.show !== 'function') return;
    // show() takes the opener to hand focus back to on close.
    var opener = document.querySelector('modal-opener[data-modal="#' + pending.id + '"] button');
    // The fallback post landed (Shopify redirects back with customer_posted=true):
    // swap the reopened modal to its success panel.
    var form = modal.querySelector(FORM_SELECTOR);
    if (form && /[?&](customer|contact)_posted=true/.test(window.location.search)) showSuccess(form);
    modal.show(opener || null);
  }

  /* ---- Busy state (KKBtn is the sitewide busy helper; guarded fallback) ---- */

  function setBusy(btn) {
    if (!btn) return;
    if (window.KKBtn) { window.KKBtn.busy(btn, 'Sending…'); return; }
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
  }

  function clearBusy(btn) {
    if (!btn) return;
    if (window.KKBtn) { window.KKBtn.reset(btn); return; }
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
  }

  /* ---- Restock signup (wishlist worker) ---- */

  // Resolves 'ok' (stored), 'invalid' (the worker rejected the input — show
  // the error, don't fall back) or 'unreachable' (network / 5xx / proxy not
  // routed — use the Shopify-form fallback). Never rejects.
  function workerSubscribe(form) {
    var productId = form.dataset.kkRestockProduct;
    var email = emailOf(form);
    if (!productId || !window.fetch) return Promise.resolve('unreachable');
    return fetch(form.getAttribute('action') || RESTOCK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        email: email,
        productId: productId,
        variantId: form.dataset.kkRestockVariant || null,
      }),
    }).then(function (r) {
      if (r.ok) return 'ok';
      if (r.status === 400 || r.status === 429) return 'invalid';
      return 'unreachable';
    }, function () { return 'unreachable'; });
  }

  function emailOf(form) {
    var input = form.querySelector('input[type="email"]');
    return input ? input.value.trim() : '';
  }

  function showError(form, show) {
    var el = form.querySelector('[data-kk-notify-error]');
    if (!el) return;
    if (show) el.textContent = form.dataset.error || 'Please enter a valid email address.';
    el.hidden = !show;
    // Signed in: the account email was turned down, so show the box it came in to put it right.
    var wrap = show && form.hasAttribute('data-kk-notify-account') ? form.querySelector('[data-kk-notify-email-wrap]') : null;
    if (wrap && wrap.hidden) {
      wrap.hidden = false;
      var input = wrap.querySelector('input[type="email"]');
      if (input) input.focus();
    }
  }

  /* ---- Modal states ---- */

  function showSuccess(form) {
    var success = document.createElement('div');
    success.className = 'kk-pc__notify-modal__success';

    var check = document.createElement('span');
    check.className = 'kk-pc__notify-modal__check';
    check.setAttribute('aria-hidden', 'true');
    // Static, non-interpolated markup — safe to set as innerHTML.
    check.innerHTML = CHECK_SVG;

    var title = document.createElement('h3');
    title.className = 'kk-pc__notify-modal__success-title';
    title.textContent = "You're on the list!";

    var text = document.createElement('p');
    text.className = 'kk-pc__notify-modal__success-text';
    text.textContent =
      form.dataset.success ||
      "We'll email you the moment it's back in stock.";

    success.appendChild(check);
    success.appendChild(title);
    success.appendChild(text);

    // Same DOM shape the server renders on posted_successfully? — a single
    // .kk-pc__notify-modal__success child of the form-states container — so
    // the existing CSS applies unchanged.
    form.replaceChildren(success);
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches(FORM_SELECTOR)) return;

    event.preventDefault();
    // The form is ours alone; keep any other storefront submit hook
    // (e.g. Shopify's captcha, which re-posts forms natively) off it.
    event.stopImmediatePropagation();

    var email = emailOf(form);
    // Loose check matching the worker's; type=email alone accepts "a@b".
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showError(form, true);
      return;
    }
    showError(form, false);
    if (form.dataset.kkSubmitting === '1') return;
    form.dataset.kkSubmitting = '1';

    var submit = form.querySelector('[type="submit"]');
    setBusy(submit);

    function succeed() {
      recordSignup(formKey(form));
      clearBusy(submit);
      form.dataset.kkSubmitting = '';
      showSuccess(form);
    }

    function fail() {
      clearBusy(submit);
      form.dataset.kkSubmitting = '';
      showError(form, true);
    }

    workerSubscribe(form).then(function (result) {
      if (result === 'ok') succeed();
      else if (result === 'invalid') fail();
      else shopifyFallback();
    });

    // Worker unreachable: post the hidden Shopify customer form instead
    // (restock-* tags). Shopify bot-challenges background /contact POSTs
    // (302 → /challenge), which a background request can't complete — then
    // submit it natively so the challenge runs, parking the modal id so the
    // reloaded page re-opens it on the success panel.
    function shopifyFallback() {
      var fb = document.getElementById(form.dataset.kkFallback || '');
      if (!fb) { fail(); return; }
      var fbEmail = fb.querySelector('input[name="contact[email]"]');
      if (fbEmail) fbEmail.value = email;
      fetch(fb.action, {
        method: 'POST',
        body: new FormData(fb),
        headers: { Accept: 'text/html' },
      })
        .then(function (response) {
          if (!response.ok || response.url.indexOf('/challenge') !== -1) return submitNatively(fb);
          succeed();
        })
        .catch(function () { submitNatively(fb); });
    }

    function submitNatively(fb) {
      recordSignup(formKey(form));
      parkPending(form);
      // form.submit() skips submit handlers, so nothing can intercept it.
      fb.submit();
    }
  });

  /* ---- Initial sweep + JS-rendered cards ---- */

  function sweep() {
    forgetAvailable();
    syncTriggers();
  }

  function init() {
    sweep();
    reopenPending();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Cards can be injected after load (search results, JS-rendered grids).
  // Cheap observer: only sweeps when nodes are added, and only while there
  // are remembered signups to reconcile. Both passes are idempotent (no DOM
  // write unless a trigger's state actually changes), so the observer can't
  // feed itself.
  if (Object.keys(readSignups()).length && 'MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length) {
          sweep();
          return;
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
